import type { APIResponse, SupportedCurrency } from '@yourownmetric/shared'

import { apiSlice } from './apiSlice'

export interface AgentConfigRow {
    id: string
    projectId: string
    agentType: string
    enabled: number
    config: string
    schedule: string | null
    lastRunAt: string | null
    lastRunStatus: string | null
    createdAt: string
    updatedAt: string
}

export interface AgentReportRow {
    id: string
    agentConfigId: string
    agentType: string
    status: string
    result: string | null
    tokensUsed: number
    triggerType: string
    createdAt: string
    completedAt: string | null
}

export interface AgentUsageBreakdown {
    agentType: string
    runCount: number
    totalTokens: number
    successCount: number
    failedCount: number
}

export interface AgentUsageData {
    totalRuns: number
    includedRuns: number
    costCents: number
    retainerCents: number
    totalCostCents: number
    periodStart: string
    periodEnd: string
    currency: SupportedCurrency
    breakdown: AgentUsageBreakdown[]
}

export const agentsApi = apiSlice.injectEndpoints({
    endpoints: builder => ({
        getAgentConfigs: builder.query<AgentConfigRow[], { projectId?: string } | void>({
            query: params => {
                const projectId = params && 'projectId' in params ? params.projectId : undefined
                return projectId ? `/agents/configs?projectId=${projectId}` : '/agents/configs'
            },
            transformResponse: (res: APIResponse<AgentConfigRow[]>) => res.data!,
            providesTags: ['Agents'],
        }),

        getAgentReports: builder.query<
            AgentReportRow[],
            { limit?: number; agentType?: string; status?: string }
        >({
            query: ({ limit = 20, agentType, status }) => {
                const params = new URLSearchParams({ limit: String(limit) })
                if (agentType) {
                    params.set('agentType', agentType)
                }
                if (status) {
                    params.set('status', status)
                }
                return `/agents/reports?${params}`
            },
            transformResponse: (res: APIResponse<AgentReportRow[]>) => res.data!,
            providesTags: ['Agents'],
        }),

        getAgentReport: builder.query<AgentReportRow, string>({
            query: id => `/agents/reports/${id}`,
            transformResponse: (res: APIResponse<AgentReportRow>) => res.data!,
        }),

        createAgentConfig: builder.mutation<
            { id: string },
            {
                projectId: string
                agentType: string
                enabled?: boolean
                schedule?: string
                config?: Record<string, unknown>
            }
        >({
            query: body => ({ url: '/agents/configs', method: 'POST', body }),
            transformResponse: (res: APIResponse<{ id: string }>) => res.data!,
            invalidatesTags: ['Agents'],
        }),

        updateAgentConfig: builder.mutation<
            { id: string; updated: boolean },
            {
                id: string
                enabled?: boolean
                schedule?: string
                config?: Record<string, unknown>
            }
        >({
            query: ({ id, ...body }) => ({ url: `/agents/configs/${id}`, method: 'PATCH', body }),
            transformResponse: (res: APIResponse<{ id: string; updated: boolean }>) => res.data!,
            invalidatesTags: ['Agents'],
        }),

        deleteAgentConfig: builder.mutation<{ id: string; deleted: boolean }, string>({
            query: id => ({ url: `/agents/configs/${id}`, method: 'DELETE' }),
            transformResponse: (res: APIResponse<{ id: string; deleted: boolean }>) => res.data!,
            invalidatesTags: ['Agents'],
        }),

        triggerAgentRun: builder.mutation<
            { message: string },
            {
                projectId: string
                agentType: string
                agentConfigId?: string
            }
        >({
            query: body => ({ url: '/agents/run', method: 'POST', body }),
            transformResponse: (res: APIResponse<{ message: string }>) => res.data!,
            invalidatesTags: ['Agents'],
        }),

        submitNLQuery: builder.mutation<
            { message: string; query: string },
            {
                projectId: string
                query: string
            }
        >({
            query: body => ({ url: '/agents/nl-query', method: 'POST', body }),
            transformResponse: (res: APIResponse<{ message: string; query: string }>) => res.data!,
            invalidatesTags: ['Agents'],
        }),

        getAgentUsage: builder.query<AgentUsageData, void>({
            query: () => '/agents/usage',
            transformResponse: (res: APIResponse<AgentUsageData>) => res.data!,
            providesTags: ['Agents'],
        }),
    }),
})

export const {
    useGetAgentConfigsQuery,
    useGetAgentReportsQuery,
    useGetAgentReportQuery,
    useCreateAgentConfigMutation,
    useUpdateAgentConfigMutation,
    useDeleteAgentConfigMutation,
    useTriggerAgentRunMutation,
    useSubmitNLQueryMutation,
    useGetAgentUsageQuery,
} = agentsApi
